import { z } from 'zod';

import { pagedSchema } from './common';

// #region N10 — GET /search/facets
export const facetValueSchema = z.object({
  value: z.string(),
  count: z.number(),
});
export const facetSchema = z.object({
  field: z.string(),
  values: z.array(facetValueSchema),
});
/** FacetResponse — facets 배열로 감싸져 온다 */
export const facetsResponseSchema = z.object({
  facets: z.array(facetSchema),
});
export type Facet = z.infer<typeof facetSchema>;
export type FacetsResponse = z.infer<typeof facetsResponseSchema>;

/** topFolderId 지정 시 해당 폴더 하위로 한정 (검색어 기준 아님 — swagger 확정) */
export type GetSearchFacetsParams = {
  topFolderId?: number;
};
// #endregion

// #region E3 — GET /files/search (SearchFileResponse)
/** 파일이 속한 폴더 한 곳 (FileLocationResponse · 검색 결과 표시용) */
export const fileLocationSchema = z.object({
  folderId: z.number(),
  koreanFullPath: z.string(),
  englishFullPath: z.string(),
  /** 홈(원본) 폴더 여부 — false면 추가 소속(별칭) */
  isHome: z.boolean(),
});
export type FileLocation = z.infer<typeof fileLocationSchema>;

/**
 * 검색 결과 1건. score = 검색 랭킹 점수.
 * ⚠️ 현재 스펙에는 description·confidential·derivedCategories가 없다 —
 * 설명 표시는 백엔드 확인 필요. 소속 경로(`locations`)는 아래.
 */
export const searchFileResponseSchema = z.object({
  id: z.number(),
  originFileName: z.string(),
  uploadFileName: z.string(),
  thumbnailFileName: z.string().nullish(),
  thumbnailUrl: z.string().nullish(),
  fileSize: z.number(),
  mimeType: z.string().nullish(),
  extension: z.string().nullish(),
  downloadCount: z.number(),
  score: z.number(),
  /**
   * 읽기 권한 — false면 잠금(검색엔 보이지만 열 수 없음 · 썸네일 없음).
   * 백엔드 develop `5534d40`(2026-11-02)부터 검색은 권한과 무관하게 노출한다.
   * 이 필드가 없는 서버는 권한 필터된 결과만 주므로 true로 본다.
   */
  canRead: z
    .boolean()
    .nullish()
    .transform((v) => v ?? true),
  /** 다운로드 권한 — false면 장바구니에 담아도 받을 수 없다(없는 서버면 true) */
  canDownload: z
    .boolean()
    .nullish()
    .transform((v) => v ?? true),
  isRepresentative: z.boolean(),
  representativeFileId: z.number().nullable(),
  relativePath: z.string().nullish(),
  associatedCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * 소속 폴더 전체 경로 — 첫 항목이 홈(원본), 이어서 추가 소속(별칭 · N4).
   * 백엔드 develop `c7faf99`(2026-11-02)에서 추가 — 읽기 권한과 무관하게 모든 소속을 준다
   * (상세 `GET /files/{id}`의 `folders`는 읽기 권한 있는 폴더만). 이 필드가 없는 서버면
   * 빈 배열이라 화면에 '-'로 보인다.
   */
  locations: z
    .array(fileLocationSchema)
    .nullish()
    .transform((v) => v ?? []),
});
export const v2SearchResultPageSchema = pagedSchema(searchFileResponseSchema);
export type SearchFileResponse = z.infer<typeof searchFileResponseSchema>;
export type V2SearchResultPage = z.infer<typeof v2SearchResultPageSchema>;

export type GetFileSearchParams = {
  searchWords: string;
  pageNumber: number;
  pageSize: number;
  /** 지정 시 해당 top 폴더 하위로 한정 */
  topFolderId?: number;
};
// #endregion

// #region N8 — POST /search (하이브리드 Dense+Sparse 검색 · 2026-08-24 신규)
/**
 * 하이브리드 검색 바디(HybridSearchRequest). 응답은 GET /files/search와 동일한
 * PaginatedList<SearchFileResponse>라 v2SearchResultPageSchema를 그대로 쓴다.
 *
 * ⚠️ R2·R3 미반영은 그대로다 — 응답에 paths·locked·description·confidential·
 * derivedCategories가 없고, 파셋 필터 파라미터도 없다.
 */
export type HybridSearchBody = {
  query: string;
  /** 지정 시 해당 top 폴더 하위로 한정 */
  topFolderId?: number | null;
  pageNumber: number;
  pageSize: number;
};
// #endregion
